import { useState, useEffect, useRef, MutableRefObject } from "react";
import { Task, SubTask } from "../types";
import { prefersReducedMotion } from "../lib/prefersReducedMotion";

interface UseTaskExitAnimationOptions {
  tasks: Task[];
  taskId: string | undefined;
  showEditDialog: boolean;
  updateSubtaskInDb: (
    id: string,
    updates: Partial<SubTask>,
  ) => Promise<unknown>;
  toggleTaskInDb: (id: string) => Promise<unknown>;
}

interface UseTaskExitAnimationReturn {
  /** ID of the task whose exit animation the corresponding TaskCard should play. */
  exitAnimatingTaskId: string | null;
  /** Pending state for the "complete with incomplete subtasks" confirmation dialog. */
  pendingComplete: { taskId: string } | null;
  /** Open the incomplete-subtasks dialog for the given task. */
  beginPendingComplete: (taskId: string) => void;
  /** Cancel the pending-complete dialog without doing anything. */
  cancelPendingComplete: () => void;
  /** Complete all subtasks and then trigger the parent task's exit animation. */
  confirmCompleteWithSubtasks: () => Promise<void>;
  /**
   * True when the task dialog is already open and the user is navigating
   * prev/next — suppresses the entrance animation to avoid a flicker.
   */
  isNavigatingTask: boolean;
  /** Ref that must be updated to the current pathname before opening a task dialog. */
  viewBeforeTaskOpen: MutableRefObject<string>;
}

export function useTaskExitAnimation({
  tasks,
  taskId,
  showEditDialog,
  updateSubtaskInDb,
  toggleTaskInDb,
}: UseTaskExitAnimationOptions): UseTaskExitAnimationReturn {
  const viewBeforeTaskOpen = useRef<string>("/");
  const prevOpenTaskIdRef = useRef<string | null>(null);

  // Computed during render using the ref's value before the post-render effect
  // updates it — this correctly reflects whether we were *already* in the dialog.
  const isNavigatingTask =
    prevOpenTaskIdRef.current !== null &&
    !!taskId &&
    prevOpenTaskIdRef.current !== taskId;

  useEffect(() => {
    prevOpenTaskIdRef.current = showEditDialog && taskId ? taskId : null;
  }, [showEditDialog, taskId]);

  const [pendingComplete, setPendingComplete] = useState<{
    taskId: string;
  } | null>(null);

  // When the user confirms the "complete with incomplete subtasks" dialog,
  // we ask the corresponding TaskCard to play its exit animation before the
  // actual completion runs — so the celebratory animation fires only after
  // the user commits, not before.
  const [exitAnimatingTaskId, setExitAnimatingTaskId] = useState<string | null>(
    null,
  );

  // Clear the exit-animation marker once the task has been toggled to
  // completed (or removed). Prevents re-triggering the animation if the
  // same task is later re-opened/uncompleted.
  useEffect(() => {
    if (!exitAnimatingTaskId) return;
    const t = tasks.find((x) => x.id === exitAnimatingTaskId);
    if (!t || t.completed) {
      setExitAnimatingTaskId(null);
    }
  }, [tasks, exitAnimatingTaskId]);

  const confirmCompleteWithSubtasks = async () => {
    if (!pendingComplete) return;
    const task = tasks.find((t) => t.id === pendingComplete.taskId);
    if (!task) {
      setPendingComplete(null);
      return;
    }
    const incomplete = task.subtasks?.filter((s) => !s.completed) ?? [];
    await Promise.all(
      incomplete.map((s) => updateSubtaskInDb(s.id, { completed: true })),
    );
    // Re-read the task here: if another tab/device already completed the
    // parent between dialog open and confirm, toggling would flip it back
    // to incomplete. Skip the parent toggle in that case.
    const latest = tasks.find((t) => t.id === pendingComplete.taskId);
    const parentTaskId = pendingComplete.taskId;
    setPendingComplete(null);
    if (latest && !latest.completed) {
      if (prefersReducedMotion()) {
        // Skip the visual animation; go straight to the DB write.
        await toggleTaskInDb(parentTaskId);
      } else {
        // Trigger the TaskCard's exit animation; it will call onToggle (i.e.
        // toggleTask) at the end, which — with all subtasks now completed —
        // will toggle the parent in the DB without reopening this dialog.
        setExitAnimatingTaskId(parentTaskId);
      }
    }
  };

  return {
    exitAnimatingTaskId,
    pendingComplete,
    beginPendingComplete: (taskId: string) => setPendingComplete({ taskId }),
    cancelPendingComplete: () => setPendingComplete(null),
    confirmCompleteWithSubtasks,
    isNavigatingTask,
    viewBeforeTaskOpen,
  };
}
